import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';

import { VbLxpSwitchComponent } from '../controls/switch/vb-lxp-switch.component';
import {
	IUpdateResponse,
	VbLxpAdminSettings,
	VbLxpAdminSettingsService,
	StreamingEntry,
	StreamingEcsService,
} from './vb-lxp-admin-settings.service';
import { VbLxpStreamingTableComponent } from './vb-lxp-streaming-table.component';
import { VbLxpStreamingEcsTableComponent } from './vb-lxp-streaming-ecs-table.component';

type SaveSettingsOptions = {
	errorMessage: string;
	successToastMessage?: string;
	onSuccess?: (settings: VbLxpAdminSettings) => void;
	setSavingState?: (saving: boolean) => void;
};

@Component({
	selector: 'vb-lxp-admin',
	standalone: true,
	imports: [CommonModule, FormsModule, VbLxpSwitchComponent, VbLxpStreamingTableComponent, VbLxpStreamingEcsTableComponent],
	providers: [VbLxpAdminSettingsService],
	templateUrl: './vb-lxp-admin.component.html',
	styleUrls: ['./vb-lxp-admin.component.css'],
})
export class VbLxpAdminComponent implements OnInit {
	private readonly adminSettingsService = inject(VbLxpAdminSettingsService);
	private readonly route = inject(ActivatedRoute);

	protected readonly settingsLoading = signal(false);
	protected readonly settingsError = signal<string | null>(null);
	protected readonly settings = signal<VbLxpAdminSettings | null>(null);

	protected readonly activeMenu = signal<'streaming' | 'streaming-ecs' | 'notification'>('streaming');
	protected readonly showStreamingEcsMenu = signal(false);
	protected readonly notificationMessageDraft = signal('');
	protected readonly showToast = signal(false);
	protected readonly toastMessage = signal('');
	protected readonly isNotificationSaving = signal(false);
	protected readonly isDragging = signal(false);
	protected readonly isUploadSaving = signal(false);
	protected readonly uploadError = signal<string | null>(null);
	protected readonly uploadedFileName = signal<string | null>(null);
	protected readonly uploadedStreamingData = signal<StreamingEntry[] | null>(null);
	protected readonly streamingEcsServices = signal<StreamingEcsService[] | null>(null);
	protected readonly streamingEcsLoading = signal(false);
	private toastTimeout: ReturnType<typeof setTimeout> | null = null;

	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

	protected readonly driverStreamStatus = computed(
		() => !(this.settings()?.race?.disableStream ?? false),
	);
	protected readonly notificationMessageLength = computed(
		() => this.notificationMessageDraft().length,
	);
	protected readonly canSendNotification = computed(
		() => this.notificationMessageDraft().trim().length > 0 && !this.isNotificationSaving(),
	);
	protected readonly expectedJsonStructure = computed(() => {
		const data = this.uploadedStreamingData();
		return data && data.length > 0 ? data : null;
	});

	ngOnInit(): void {
		this.showStreamingEcsMenu.set(Boolean(this.route.snapshot.data['showStreamingEcsMenu']));

		if (!this.showStreamingEcsMenu() && this.activeMenu() === 'streaming-ecs') {
			this.activeMenu.set('streaming');
		}

		this.loadSettings().subscribe({
			error: (error: { status?: number }) =>
				this.handleSettingsError(error, 'Unable to load admin settings.'),
		});
	}

	public saveDriverStreamStatus = (checked: boolean): Observable<IUpdateResponse> => {
		return this.saveSettingsUpdate(
			{
				race: {
					disableStream: !checked,
				},
			},
			{
				errorMessage: 'Unable to toggle Driver Video Stream',
				successToastMessage: checked
					? 'Driver Video Stream enabled'
					: 'Driver Video Stream disabled',
			},
		);
	};

	public onNotificationMessageChanged(message: string): void {
		this.notificationMessageDraft.set((message ?? '').slice(0, 300));
	}

	public onSendToSiteClicked(): void {
		this.saveNotificationMessage(this.notificationMessageDraft());
	}

	public onClearBannerMessageClicked(): void {
		this.saveNotificationMessage('');
	}

	public displayToast(message: string): void {
		if (this.toastTimeout) {
			clearTimeout(this.toastTimeout);
		}

		this.toastMessage.set(message);
		this.showToast.set(true);

		this.toastTimeout = setTimeout(() => {
			this.showToast.set(false);
			this.toastTimeout = null;
		}, 3000);
	}

	private saveNotificationMessage(message: string): void {
		const isClearing = message === '';
		const nextMessage = message ?? '';

		this.saveSettingsUpdate(
			{
				notificationMessage: nextMessage,
			},
			{
				errorMessage: isClearing
					? 'Unable to clear banner message from site'
					: 'Unable to send banner message to site',
				successToastMessage: isClearing
					? 'Banner message cleared from site'
					: 'Banner message sent to site',
				setSavingState: (saving) => this.isNotificationSaving.set(saving),
				onSuccess: (settings) => {
					this.notificationMessageDraft.set(settings.notificationMessage ?? nextMessage);
				},
			},
		).subscribe({
			error: (error) => console.error('Notification settings save failed:', error),
		});
	}

	public clearSettingsError(): void {
		this.settingsError.set(null);
	}

	public browseFile(): void {
		this.fileInput.nativeElement.click();
	}

	public onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(true);
	}

	public onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(false);
	}

	public onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragging.set(false);
		const file = event.dataTransfer?.files?.[0];
		if (file) this.processJsonFile(file);
	}

	public onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (file) this.processJsonFile(file);
		input.value = '';
	}

	private processJsonFile(file: File): void {
		this.uploadError.set(null);
		if (!file.name.endsWith('.json')) {
			this.uploadError.set('Only .json files are accepted');
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			this.uploadError.set('File exceeds the 10 MB limit');
			return;
		}
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const parsed = JSON.parse(e.target?.result as string);
				const streamingData: StreamingEntry[] = Array.isArray(parsed) ? parsed : [parsed];
				this.uploadedFileName.set(file.name);
				this.uploadedStreamingData.set(streamingData);
				this.saveStreamingData(streamingData);
			} catch {
				this.uploadError.set('Invalid JSON — please check the file and try again');
			}
		};
		reader.readAsText(file);
	}

	private saveStreamingData(streamingData: StreamingEntry[]): void {
		this.saveSettingsUpdate(
			{ streamingData },
			{
				errorMessage: 'Unable to upload streaming config',
				successToastMessage: 'Streaming config uploaded successfully',
				setSavingState: (saving) => this.isUploadSaving.set(saving),
			},
		).subscribe({
			error: (error) => console.error('Streaming config upload failed:', error),
		});
	}

	public clearStreamingData(): void {
		this.uploadedStreamingData.set(null);
		this.uploadedFileName.set(null);
		this.uploadError.set(null);

		this.saveSettingsUpdate(
			{ streamingData: [] },
			{
				errorMessage: 'Unable to clear streaming config',
				successToastMessage: 'Streaming config cleared',
				setSavingState: (saving) => this.isUploadSaving.set(saving),
			},
		).subscribe({
			error: (error) => console.error('Streaming config clear failed:', error),
		});
	}

	public setActiveMenu(menu: 'streaming' | 'streaming-ecs' | 'notification'): void {
		if (menu === 'streaming-ecs' && !this.showStreamingEcsMenu()) {
			return;
		}

		this.activeMenu.set(menu);
		
		// Load streaming ECS services when that menu is selected
		if (menu === 'streaming-ecs') {
			this.loadStreamingEcsServices();
		}
	}

	private saveSettingsUpdate(
		payload: Partial<VbLxpAdminSettings>,
		options: SaveSettingsOptions,
	): Observable<IUpdateResponse> {
		this.settingsError.set(null);
		options.setSavingState?.(true);

		return this.adminSettingsService
			.updateSettings(payload)
			.pipe(
				tap((response) => {
					this.patchSettings(response.settings);
					options.onSuccess?.(response.settings);
					this.displayToast(options.successToastMessage ?? 'Admin settings saved');
					this.settingsError.set(null);
				}),
				catchError((err: { status?: number }) => {
					this.handleSettingsError(err, options.errorMessage);
					return throwError(() => err);
				}),
				finalize(() => options.setSavingState?.(false)),
			);
	}

	private patchSettings(nextPartial: Partial<VbLxpAdminSettings>): void {
		this.settings.update((current) => {
			const currentSettings = current ?? {};
			const merged: VbLxpAdminSettings = {
				...currentSettings,
				...nextPartial,
				race: {
					...(currentSettings.race ?? {}),
					...(nextPartial.race ?? {}),
				},
			};
			return merged;
		});
	}

	private handleSettingsError(error: { status?: number }, message: string) {
		if (error.status === 401) {
			// Interceptor already cleared token and redirected, just show error
			this.settingsError.set('Session expired. Please login again.');
		} else {
			this.settingsError.set(message);
		}
	}

	private loadSettings() {
		this.settingsLoading.set(true);
		this.settingsError.set(null);

		return this.adminSettingsService.getSettings().pipe(
			tap((settings) => {
				this.settings.set(settings);
				this.notificationMessageDraft.set(settings.notificationMessage ?? '');
				if (settings.streamingData?.length) {
					this.uploadedStreamingData.set(settings.streamingData);
				}
				this.settingsError.set(null);
			}),
			finalize(() => this.settingsLoading.set(false)),
		);
	}

	public loadStreamingEcsServices(): void {
		this.streamingEcsLoading.set(true);
		
		this.adminSettingsService.getStreamingEcsServices()
			.pipe(
				tap((response) => {
					console.log('[StreamingECS] Services loaded:', response.services);
					this.streamingEcsServices.set(response.services);
				}),
				catchError((error) => {
					console.error('[StreamingECS] Error loading services:', error);
					return throwError(() => error);
				}),
				finalize(() => this.streamingEcsLoading.set(false)),
			)
			.subscribe();
	}
}
